// The paste event already grants access to its image files; never read the
// system clipboard here. Capture file references before the event returns.
import * as fs from "../node/fs.ts";

const IMAGE_EXTENSIONS: Record<string, string> = {
	"image/png": "png", "image/jpeg": "jpg", "image/webp": "webp", "image/gif": "gif",
	"image/bmp": "bmp", "image/x-ms-bmp": "bmp", "image/tiff": "tiff", "image/avif": "avif",
	"image/svg+xml": "svg", "image/heic": "heic", "image/heif": "heif",
};

export function isBrowserPasteKey(event: Pick<KeyboardEvent, "key" | "ctrlKey" | "metaKey" | "altKey" | "isComposing">, platform: string): boolean {
	if (event.isComposing || event.altKey || event.key.toLowerCase() !== "v") return false;
	return /Mac|iPhone|iPad|iPod/.test(platform) ? event.metaKey && !event.ctrlKey : event.ctrlKey && !event.metaKey;
}

export interface ImagePasteOptions {
	input(sequence: string): void;
	error(message: string): void;
}

/** Returns a disposer that also prevents an unfinished read from pasting after disposal. */
export function installImagePaste(target: HTMLElement, options: ImagePasteOptions): () => void {
	let disposed = false;
	let pending = Promise.resolve();
	const onPaste = (event: ClipboardEvent) => {
		const data = event.clipboardData;
		if (!data) return;
		let images = Array.from(data.files).filter((file) => file.type.startsWith("image/"));
		if (!images.length) {
			images = Array.from(data.items)
				.filter((item) => item.kind === "file" && item.type.startsWith("image/"))
				.map((item) => item.getAsFile()).filter((file): file is File => file !== null);
		}
		if (!images.length) return; // Plain text stays with xterm's original handler.
		event.preventDefault();
		event.stopImmediatePropagation();
		pending = pending.then(async () => {
			const paths: string[] = [];
			try {
				for (const image of images) {
					const bytes = new Uint8Array(await image.arrayBuffer());
					if (disposed) return;
					const extension = IMAGE_EXTENSIONS[image.type.toLowerCase()] ?? "img";
					const path = `/tmp/pi-clipboard-${globalThis.crypto.randomUUID()}.${extension}`;
					fs.mkdirSync("/tmp", { recursive: true });
					fs.writeFileSync(path, bytes, { flag: "wx", mode: 0o600 });
					paths.push(path);
				}
				options.input(`\x1b[200~${paths.join("\n")}\x1b[201~`);
			} catch (error) {
				// Keep successfully written images usable if a later image fails.
				if (!disposed && paths.length) options.input(`\x1b[200~${paths.join("\n")}\x1b[201~`);
				if (!disposed) options.error(error instanceof Error ? error.message : String(error));
			}
		});
	};
	target.addEventListener("paste", onPaste, { capture: true });
	return () => {
		disposed = true;
		target.removeEventListener("paste", onPaste, { capture: true });
	};
}
